import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Calculator, Copy, Mail, Trash2 } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const STATUS_META = {
  pending: { color: "hsl(var(--warning))" },
  accepted: { color: "hsl(var(--success))" },
  revoked: { color: "hsl(var(--destructive))" },
};

export default function Accountant() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [lastLink, setLastLink] = useState(null);

  const { data: invites = [] } = useQuery({
    queryKey: ["accountant-invites"],
    queryFn: async () => (await api.get("/accountant/invites")).data,
  });

  const invite = useMutation({
    mutationFn: (email) => api.post("/accountant/invite", { email }),
    onSuccess: (res) => {
      setLastLink(res.data.invite_url);
      setEmail("");
      qc.invalidateQueries({ queryKey: ["accountant-invites"] });
    },
    onError: (e) => toast.error(apiErrorMessage(e.response?.data?.detail)),
  });

  const revoke = useMutation({
    mutationFn: (id) => api.delete(`/accountant/invites/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["accountant-invites"] }),
  });

  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success(t("link_copied"));
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="accountant-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-sm bg-secondary flex items-center justify-center">
          <Calculator className="h-5 w-5 text-[hsl(var(--success))]" />
        </div>
        <div>
          <h1 className="font-display text-4xl">{t("accountant_title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("accountant_sub")}</p>
        </div>
      </div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <Card className="p-6 rounded-sm border-border space-y-4">
          <Label htmlFor="accountant-email">{t("accountant_invite_email_label")}</Label>
          <div className="flex flex-wrap gap-2">
            <Input id="accountant-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="contabilista@email.com" data-testid="accountant-email-input" className="rounded-sm max-w-xs" />
            <Button onClick={() => email && invite.mutate(email)} disabled={invite.isPending || !email}
              data-testid="accountant-invite-btn" className="rounded-sm gap-2">
              <Mail className="h-4 w-4" /> {t("accountant_send_invite")}
            </Button>
          </div>
          {lastLink && (
            <div className="flex items-center gap-2 p-3 rounded-sm bg-secondary text-sm" data-testid="accountant-invite-link">
              <span className="truncate flex-1 font-mono text-xs">{lastLink}</span>
              <Button variant="outline" size="icon" onClick={() => copyLink(lastLink)} className="rounded-sm shrink-0">
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          )}
        </Card>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
        <Card className="rounded-sm border-border overflow-hidden">
          {invites.length === 0 ? (
            <p className="text-sm text-muted-foreground p-6 text-center">{t("empty_generic")}</p>
          ) : (
            <div className="divide-y divide-border">
              {invites.map((i) => (
                <div key={i.id} className="flex items-center justify-between px-6 py-4" data-testid={`invite-${i.id}`}>
                  <div>
                    <p className="text-sm font-medium">{i.email}</p>
                    <p className="text-xs text-muted-foreground">{fmtDate(i.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="rounded-sm" style={{ color: STATUS_META[i.status]?.color, borderColor: STATUS_META[i.status]?.color }}>
                      {t(`accountant_status_${i.status}`)}
                    </Badge>
                    {i.status !== "revoked" && (
                      <Button variant="ghost" size="icon" onClick={() => revoke.mutate(i.id)} data-testid={`revoke-${i.id}`}
                        className="text-muted-foreground hover:text-[hsl(var(--destructive))]">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </motion.div>
    </div>
  );
}
